import React from 'react';
import {AlertTriangleIcon, CalendarIcon, CheckCheckIcon, SparklesIcon} from '../icons';
import {CompatWarning} from './BuilderCompatPanel';

/**
 * Три раздельных сигнала готовности образа (UX-аудит F05).
 *
 * Аудит поймал конструктор на том, что «Ваш образ готов» показывалось для комплекта из
 * повреждённых шорт в очереди на выбытие: высокая стилистическая сочетаемость выдавалась
 * за пригодность к носке. Поэтому сигналы разведены и ни один не подменяет другой:
 *
 * 1. «Сочетается» — балл движка оценки;
 * 2. «Повод и сезон» — пересечение атрибутов вещей комплекта; неполные данные честно
 *    называются неполными, а не «подходит»;
 * 3. «Вещи доступны и пригодны» — состояние, дефекты, корзина, очередь выбытия, статус.
 *
 * Запрета нет: рабочую одежду собрать можно, но ограничение видно и рядом есть «Заменить».
 */

export type SignalTone = 'ok' | 'warn' | 'unknown';

interface Props {
    /** Живая оценка образа: балл 0–100 и метка шкалы. */
    score: number | null | undefined;
    scoreLabel: string | null | undefined;
    scoreLoading: boolean;
    scoreError: string | null;
    /** Характеристики комплекта из `POST /api/compatibility/for-set`. */
    sharedOccasions: string[];
    sharedSeasons: string[];
    occasionDataComplete: boolean;
    seasonDataComplete: boolean;
    /** Ограничения пригодности вещей комплекта. */
    warnings: CompatWarning[];
    /** Загрузка характеристик комплекта (сигналы 2 и 3). */
    setLoading: boolean;
    /** Переход к замене конкретной вещи; `undefined` — действие недоступно. */
    onReplace?: (itemId: string) => void;
    /**
     * Закрыты ли обязательные слоты комплекта (ретест 17.09.2026, R11). Для частичного набора
     * подпись — «Сочетаемость выбранных вещей»: слово «цельность» обещает оценку целого образа,
     * а футболка с худи без низа и обуви целым образом не являются.
     */
    complete?: boolean;
}

const TONE_CLASS: Record<SignalTone, string> = {
    ok: 'text-green-700 dark:text-green-400',
    warn: 'text-amber-700 dark:text-amber-400',
    unknown: 'text-gray-500 dark:text-gray-400',
};

const UNKNOWN_TEXT = 'Данных недостаточно';

const SignalRow: React.FC<{
    icon: React.ReactNode;
    title: string;
    tone: SignalTone;
    text: string;
    testId: string;
    children?: React.ReactNode;
}> = ({icon, title, tone, text, testId, children}) => (
    <div className="flex items-start gap-2" data-testid={testId} data-tone={tone}>
        <span className={`flex-shrink-0 mt-0.5 ${TONE_CLASS[tone]}`}>{icon}</span>
        <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{title}</p>
            <p className={`text-xs ${TONE_CLASS[tone]}`}>{text}</p>
            {children}
        </div>
    </div>
);

function styleText(score: number | null | undefined, label: string | null | undefined,
                   loading: boolean, error: string | null, complete: boolean): {tone: SignalTone; text: string} {
    if (loading) {
        return {tone: 'unknown', text: 'Считаем оценку…'};
    }
    if (error) {
        return {tone: 'unknown', text: 'Оценка недоступна'};
    }
    if (score == null) {
        return {tone: 'unknown', text: UNKNOWN_TEXT};
    }
    // Метка шкалы («Очень высокая цельность») относится к целому образу — для частичного
    // набора показываем только число.
    const suffix = complete && label ? ` · ${label}` : '';
    return {tone: score >= 60 ? 'ok' : 'warn', text: `${Math.round(score)} из 100${suffix}`};
}

/** Заголовок сигнала сочетаемости: целый образ или только выбранные вещи. */
export function styleTitle(complete: boolean): string {
    return complete ? 'Сочетается' : 'Сочетаемость выбранных вещей';
}

function occasionText(shared: string[], complete: boolean, loading: boolean): {tone: SignalTone; text: string} {
    if (loading) {
        return {tone: 'unknown', text: 'Проверяем…'};
    }
    if (!complete) {
        return {tone: 'unknown', text: `${UNKNOWN_TEXT}: поводы или сезоны заполнены не у всех вещей`};
    }
    if (shared.length === 0) {
        return {tone: 'warn', text: 'Общего повода или сезона у вещей нет'};
    }
    return {tone: 'ok', text: shared.join(', ')};
}

const OutfitReadinessSignals: React.FC<Props> = ({
    score, scoreLabel, scoreLoading, scoreError,
    sharedOccasions, sharedSeasons, occasionDataComplete, seasonDataComplete,
    warnings, setLoading, onReplace, complete = true,
}) => {
    const style = styleText(score, scoreLabel, scoreLoading, scoreError, complete);
    const shared = [...sharedOccasions, ...sharedSeasons];
    const occasion = occasionText(shared, occasionDataComplete && seasonDataComplete, setLoading);
    const fitness: {tone: SignalTone; text: string} = setLoading
        ? {tone: 'unknown', text: 'Проверяем…'}
        : warnings.length === 0
            ? {tone: 'ok', text: 'Все вещи в строю'}
            : {tone: 'warn', text: `Ограничений: ${warnings.length}`};

    return (
        <div className="rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-900 p-3 space-y-2.5"
             data-testid="outfit-readiness-signals">
            <SignalRow
                testId="signal-style"
                icon={<SparklesIcon className="w-4 h-4"/>}
                title={styleTitle(complete)}
                tone={style.tone}
                text={style.text}
            />
            <SignalRow
                testId="signal-occasion"
                icon={<CalendarIcon className="w-4 h-4"/>}
                title="Повод и сезон"
                tone={occasion.tone}
                text={occasion.text}
            />
            <SignalRow
                testId="signal-fitness"
                icon={fitness.tone === 'ok'
                    ? <CheckCheckIcon className="w-4 h-4"/>
                    : <AlertTriangleIcon className="w-4 h-4"/>}
                title="Вещи доступны и пригодны"
                tone={fitness.tone}
                text={fitness.text}
            >
                {warnings.length > 0 && (
                    <ul className="mt-1 space-y-1">
                        {warnings.map(warning => (
                            <li key={`${warning.itemId}-${warning.code}`}
                                className="flex flex-wrap items-center gap-x-2 text-xs text-gray-600 dark:text-gray-300">
                                <span>«{warning.itemName}»: {warning.message}</span>
                                {onReplace && (
                                    <button
                                        type="button"
                                        onClick={() => onReplace(warning.itemId)}
                                        aria-label={`Заменить «${warning.itemName}» в образе`}
                                        className="min-h-[44px] sm:min-h-0 sm:py-1 px-2 rounded-lg text-brand-600
                                                   dark:text-brand-400 hover:bg-gray-100 dark:hover:bg-ink-800
                                                   transition-colors"
                                    >
                                        Заменить
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </SignalRow>
        </div>
    );
};

export default OutfitReadinessSignals;
